import React, { useEffect, useState } from 'react'
import { IonIcon } from '@ionic/react'
import { Form, useFormik, FormikProvider } from 'formik'
import { useHistory, useParams } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import { useSelector, useDispatch } from 'react-redux'
import styled from '@emotion/styled'
import { arrowForwardOutline, eyeOffOutline, eyeOutline } from 'ionicons/icons'
import { authActions } from './AuthStore'
import axios from '../common/axios'
import { Yup } from '../common/Yup'
import { IStateRedux } from '../common/ReduxStore'
import { AuthPageLayout } from './AuthPageLayout'
import {
  AUTH_COLORS,
  AuthField,
  AuthFormBox,
  AuthPasswordToggle,
  AuthPasswordWrap,
  AuthPrimaryButton,
  AuthSubtitle,
  AuthTitle,
} from './AuthForm'

export const MudarSenhaToken = () => {
  const { PasswordRule } = MudarSenhaToken
  const { token } = useParams<{ token: string }>()
  const dispatch = useDispatch()
  const actions = bindActionCreators(authActions, dispatch)
  const loading = useSelector((s: IStateRedux) => s.auth.request.fetching)
  const senhaMudada = useSelector((s: IStateRedux) => s.auth.senhaMudada)
  const history = useHistory()
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false)

  useEffect(() => {
    if (!senhaMudada) return

    const timeout = setTimeout(() => history.push('/'), 1500)
    return () => clearTimeout(timeout)
  }, [history, senhaMudada])

  useEffect(() => {
    axios.Usuarios.getByToken(token)
      .then((data) => {
        if (!data) history.push('/')
      })
      .catch(() => history.push('/'))
  }, [history, token])

  const formik = useFormik({
    initialValues: { novaSenha: '', novaSenhaConfirmacao: '' },
    validateOnMount: true,
    validationSchema: Yup.object().shape({
      novaSenha: Yup.string()
        .required('Nova senha requerida')
        .min(6, 'A senha precisa ter pelo menos 6 caracteres'),
      novaSenhaConfirmacao: Yup.string()
        .required('Confirmação da Nova senha requerida')
        .oneOf([Yup.ref('novaSenha'), null], 'Senhas incompatíveis'),
    }),
    onSubmit: (values) => {
      const { novaSenha } = values
      actions.mudarSenhaToken({ senha: novaSenha, token: token || '' })
    },
  })

  const senhaError =
    formik.touched.novaSenha && formik.errors.novaSenha ? formik.errors.novaSenha : ''
  const senhaConfirmacaoError =
    formik.touched.novaSenhaConfirmacao && formik.errors.novaSenhaConfirmacao
      ? formik.errors.novaSenhaConfirmacao
      : ''
  const senhaTemTamanhoMinimo = formik.values.novaSenha.length >= 6
  const isSubmitDisabled =
    loading || !formik.isValid || !formik.values.novaSenha || !formik.values.novaSenhaConfirmacao

  return (
    <AuthPageLayout variant="recovery">
      <AuthFormBox>
        <AuthTitle>
          <strong>Crie sua nova senha</strong>
        </AuthTitle>
        <AuthSubtitle>
          Quase lá. Digite uma senha fácil de lembrar para voltar aos seus estudos.
        </AuthSubtitle>
        <FormikProvider value={formik}>
          <Form noValidate onSubmit={formik.handleSubmit}>
            <AuthField hasError={!!senhaError}>
              <label htmlFor="novaSenha">Senha</label>
              <AuthPasswordWrap>
                <input
                  id="novaSenha"
                  name="novaSenha"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Nova senha"
                  value={formik.values.novaSenha}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  aria-invalid={!!senhaError}
                />
                <AuthPasswordToggle
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  <IonIcon icon={showPassword ? eyeOutline : eyeOffOutline} />
                </AuthPasswordToggle>
              </AuthPasswordWrap>
              {senhaError ? <p className="field-error">{senhaError}</p> : null}
            </AuthField>
            <AuthField hasError={!!senhaConfirmacaoError}>
              <label htmlFor="novaSenhaConfirmacao">
                Confirme sua nova senha <span>(Digite exatamente a mesma senha acima.)</span>
              </label>
              <AuthPasswordWrap>
                <input
                  id="novaSenhaConfirmacao"
                  name="novaSenhaConfirmacao"
                  type={showPasswordConfirmation ? 'text' : 'password'}
                  placeholder="Confirme sua nova senha"
                  value={formik.values.novaSenhaConfirmacao}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  aria-invalid={!!senhaConfirmacaoError}
                />
                <AuthPasswordToggle
                  type="button"
                  onClick={() => setShowPasswordConfirmation((current) => !current)}
                  aria-label={showPasswordConfirmation ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  <IonIcon icon={showPasswordConfirmation ? eyeOutline : eyeOffOutline} />
                </AuthPasswordToggle>
              </AuthPasswordWrap>
              {senhaConfirmacaoError ? (
                <p className="field-error">{senhaConfirmacaoError}</p>
              ) : null}
            </AuthField>
            <PasswordRule isValid={senhaTemTamanhoMinimo}>
              <span />A senha precisa ter pelo menos 6 números ou letras.
            </PasswordRule>
            <AuthPrimaryButton type="submit" disabled={isSubmitDisabled}>
              {loading ? (
                'SALVANDO...'
              ) : (
                <>
                  SALVAR E ACESSAR AULA
                  <IonIcon icon={arrowForwardOutline} />
                </>
              )}
            </AuthPrimaryButton>
          </Form>
        </FormikProvider>
      </AuthFormBox>
    </AuthPageLayout>
  )
}

MudarSenhaToken.PasswordRule = styled.p<{ isValid: boolean }>(
  (props) => `
  margin: 8px 0 0;
  color: ${props.isValid ? '#34a853' : AUTH_COLORS.blueMuted};
  font-size: 12px;
  line-height: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: color 0.2s ease;

  span {
    width: 11px;
    height: 11px;
    border: 1px solid ${props.isValid ? '#34a853' : 'rgba(113, 127, 141, 1)'};
    border-radius: 2px;
    background: ${props.isValid ? 'rgba(52, 168, 83, 0.5)' : 'rgba(113, 127, 141, 0.2)'};
    display: inline-block;
    flex: 0 0 auto;
    transition: border-color 0.2s ease, background 0.2s ease;
  }
`
)
